import React, { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from '../../../../lib/utils';
import { reportsApi } from '../../api/reports';
import TableJoinModal from './TableJoinModal';
import {
  LEVEL_LABELS, WIDTH_PRESETS, HEIGHT_PRESETS, colLabel, visibleColumnKeys, withPersianLabel,
} from './constants';
import { EDITOR_KIND, getWidgetEditorKind, shouldSkipWidgetDataFetch, isChartWidgetType } from '../../widgets/registry';
import { errorsForWidget } from '../../utils/validateReportsConfig';
import { getWidgetSql, parseSqlJoinConfig, parseSqlSelectKeys, sqlSelectNeedsDiscover, sqlUsesPeriodParams, syncWidgetSqlFromTableColumns } from '../../utils/buildJoinSql';
import { WIDGET_THEMES, appearanceFromTheme, matchWidgetTheme, applyNakedWidgetChrome, isNakedWidgetChrome } from '../../utils/widgetThemes';
import { TABLE_PAGE_SIZE_OPTIONS } from '../../utils/widgetPreviewData';
import { chartSeries } from '../../../../shared/theme/chartPalette';
import { COLUMN_SIZE_MODES, defaultTableColumn, isCustomColumn, nextCustomColumnKey, resolveColumnSizeMode, syncTableColumnsFromQueryKeys } from '../../utils/tableColumnDisplay';
import TableColumnsEditor, { TableLookFields } from './TableColumnEditor';
import KpiCardEditor from './KpiCardEditor';
import GaugeEditor from './GaugeEditor';
import {
  REPORT_DATA_SOURCE,
  widgetUsesReportQueries,
  fieldsForWidget,
  catalogFieldsFromQueries,
  normalizeReportQueries,
  selectedReportQuery,
} from '../../utils/reportQueries';

const QUERY_LIMIT_MAX = 10000;

const APPEARANCE_PRESETS = WIDGET_THEMES;

function Section({ title, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
      >
        <span className="flex items-center gap-1.5">
          {title}
          {badge != null && badge !== '' && (
            <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold text-indigo-700">{badge}</span>
          )}
        </span>
        <span className="text-slate-300 text-[10px]">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      {label && <label className="text-[10px] font-medium text-slate-500">{label}</label>}
      {children}
    </div>
  );
}

const inputCls = 'w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300';
const selectCls = inputCls;

function ColorField({ label, value, onChange }) {
  const empty = !value;
  return (
    <Field label={label}>
      <div className="flex items-center gap-1">
        <input
          type="color"
          disabled={empty}
          value={value || '#334155'}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-8 rounded border border-slate-200 cursor-pointer disabled:opacity-40"
        />
        <button
          type="button"
          onClick={() => onChange(empty ? '#334155' : '')}
          className={cn(
            'flex-1 rounded-md border px-1.5 py-1 text-[10px] truncate',
            empty ? 'border-dashed border-slate-300 text-slate-400' : 'border-slate-200 text-slate-600',
          )}
        >
          {empty ? 'پیش‌فرض' : value}
        </button>
      </div>
    </Field>
  );
}

function parseSelected(selectArr) {
  return (selectArr || []).map((expr) => {
    const aliasMatch = /^(SUM|AVG|MAX|MIN|COUNT)\((\w+)\)\s+as\s+(\w+)$/i.exec(expr);
    if (aliasMatch) return { expr, label: aliasMatch[3], isMetric: true, agg: aliasMatch[1].toUpperCase() };
    const aggMatch = /^(SUM|AVG|MAX|MIN|COUNT)\((\w+)\)$/i.exec(expr);
    if (aggMatch) return { expr, label: aggMatch[2], isMetric: true, agg: aggMatch[1].toUpperCase() };
    return { expr, label: colLabel(expr), isMetric: false };
  });
}

function applySqlToWidget(widget, { sql, joinConfig, fields, dimensions, metrics }) {
  const keys = (fields || []).map((f) => f.key).filter(Boolean);
  const dims = dimensions || [];
  const mets = metrics || [];
  const patch = {
    dataSource: 'sql',
    sqlQuery: sql,
    joinConfig: joinConfig === undefined ? widget.joinConfig : joinConfig,
    sqlFields: {
      dimensions: dims,
      metrics: mets,
      fields: fields || [],
    },
    query: {
      ...(widget.query || {}),
      sql,
      select: keys,
      groupBy: [],
      orderBy: widget.query?.orderBy || [],
      limit: widget.query?.limit || 100,
    },
  };

  if (widget.type === 'table' && keys.length) {
    patch.tableConfig = {
      ...(widget.tableConfig || {}),
      columns: syncTableColumnsFromQueryKeys(
        widget.tableConfig?.columns || [],
        keys,
        mets,
      ),
      pageSize: widget.tableConfig?.pageSize || 20,
    };
  }

  const nextCols = patch.tableConfig?.columns || widget.tableConfig?.columns;
  if (Array.isArray(nextCols) && nextCols.some(isCustomColumn)) {
    const sqlWithCustom = syncWidgetSqlFromTableColumns(
      { ...widget, ...patch, tableConfig: { ...(widget.tableConfig || {}), ...(patch.tableConfig || {}), columns: nextCols } },
      nextCols,
    );
    if (sqlWithCustom) {
      const parsed = parseSqlSelectKeys(sqlWithCustom);
      patch.sqlQuery = sqlWithCustom;
      patch.query = {
        ...patch.query,
        sql: sqlWithCustom,
        select: parsed.length ? parsed : keys,
      };
    }
  }

  if (isChartWidgetType(widget.type) && keys.length) {
    const xKey = widget.chartConfig?.xKey || dims[0] || keys[0];
    const seriesKeys = (widget.chartConfig?.series || []).some((s) => s?.key)
      ? (widget.chartConfig.series || [])
      : (mets.length ? mets : keys.filter((k) => k !== xKey)).slice(0, 3).map((key, i) => ({
        key,
        label: colLabel(key),
        color: chartSeries[i % chartSeries.length],
      }));
    patch.chartConfig = {
      ...(widget.chartConfig || {}),
      xKey,
      series: seriesKeys,
    };
  }

  if (widget.type === 'stat_card' && keys.length && !widget.statConfig?.valueKey) {
    patch.statConfig = {
      ...(widget.statConfig || {}),
      valueKey: mets[0] || keys[0],
    };
  }

  if (widget.type === 'gauge' && keys.length) {
    patch.gaugeConfig = {
      actualKey: widget.gaugeConfig?.actualKey || mets[0] || keys[0],
      targetKey: widget.gaugeConfig?.targetKey || mets[1] || mets[0] || keys[1] || keys[0],
    };
  }

  return patch;
}

export default function WidgetEditorPanel({
  widget,
  maxLevel = 4,
  jobs = [],
  layout,
  onChange,
  onLayoutChange,
  onClose,
  validationErrors = [],
  reportQueries = [],
}) {
  const [columns, setColumns] = useState({ dimensions: [], metrics: [] });
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [sqlTables, setSqlTables] = useState([]);
  const lastDiscoverRef = useRef({ id: '', sql: '' });

  const widgetSql = getWidgetSql(widget);
  const parsedJoin = useMemo(() => parseSqlJoinConfig(widgetSql), [widgetSql]);
  const queryTables = (widget.joinConfig?.tables || []).length
    ? widget.joinConfig.tables
    : parsedJoin.tables;
  const usesPeriod = !widgetSql || sqlUsesPeriodParams(widgetSql);
  const widgetErrors = useMemo(
    () => errorsForWidget(validationErrors, widget?.id),
    [validationErrors, widget?.id],
  );
  const fieldError = (field) =>
    widgetErrors.find((e) => e.field === field || e.field?.startsWith(`${field}.`))?.message;

  useEffect(() => {
    if (fieldError('sqlQuery')) setShowJoinModal(true);
    // Intentionally only react when validation errors for this widget change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetErrors]);

  /** Jobs from settings.job — used for access/scope picker (stores org level_type). */
  const jobOptions = useMemo(() => {
    const list = (jobs || [])
      .map((j) => ({
        id: Number(j.id),
        name: j.name || `شغل ${j.id}`,
        level: Math.min(4, Math.max(1, Number(j.level_type) || 1)),
      }))
      .filter((j) => Number.isFinite(j.id))
      .sort((a, b) => a.id - b.id);
    if (list.length) return list;
    const cap = Math.min(4, Math.max(1, Number(maxLevel) || 4));
    return Array.from({ length: cap }, (_, i) => ({
      id: i + 1,
      name: LEVEL_LABELS[i + 1] || `سطح ${i + 1}`,
      level: i + 1,
    }));
  }, [jobs, maxLevel]);

  useEffect(() => {
    if (!widget || shouldSkipWidgetDataFetch(widget.type)) return undefined;
    reportsApi.tables()
      .then((res) => setSqlTables(res.data?.tables || res.data?.allowed || []))
      .catch(() => setSqlTables([]));

    const sql = getWidgetSql(widget);
    const parsed = parseSqlSelectKeys(sql);
    const dims = widget.sqlFields?.dimensions || [];
    const mets = widget.sqlFields?.metrics || [];
    const fromFields = (widget.sqlFields?.fields || [])
      .map((f) => (typeof f === 'string' ? f : f?.key))
      .filter(Boolean);
    const keys = parsed.length ? parsed : fromFields;

    if (dims.length || mets.length) {
      setColumns({ dimensions: dims, metrics: mets });
    } else if (keys.length) {
      setColumns({ dimensions: keys, metrics: [] });
    } else {
      setColumns({ dimensions: [], metrics: [] });
    }

    const missingSqlString = !!sql && !String(widget.sqlQuery || widget.query?.sql || '').trim();
    const missingFields = !!sql && !dims.length && !mets.length && !fromFields.length;
    if (missingSqlString || (missingFields && parsed.length)) {
      onChange({
        ...widget,
        sqlQuery: widget.sqlQuery || sql,
        sqlFields: {
          dimensions: dims.length ? dims : parsed,
          metrics: mets,
          fields: fromFields.length
            ? widget.sqlFields.fields
            : parsed.map((key) => ({ key, fa: colLabel(key) })),
        },
        query: {
          ...(widget.query || {}),
          sql: widget.query?.sql || sql,
          select: (widget.query?.select || []).length ? widget.query.select : parsed,
        },
      });
      return undefined;
    }

    const alreadyDiscovered = lastDiscoverRef.current.id === widget.id
      && lastDiscoverRef.current.sql === sql;
    const needDiscover = sqlSelectNeedsDiscover(sql) && !alreadyDiscovered;
    if (!needDiscover && (!sql || keys.length || dims.length || mets.length)) return undefined;

    lastDiscoverRef.current = { id: widget.id, sql };
    let cancelled = false;
    reportsApi.discoverSql(sql)
      .then((res) => {
        if (cancelled) return;
        const discoveredDims = res.data?.dimensions || [];
        const discoveredMets = res.data?.metrics || [];
        const fields = res.data?.fields || [];
        if (!discoveredDims.length && !discoveredMets.length && !fields.length) return;
        setColumns({ dimensions: discoveredDims, metrics: discoveredMets });
        onChange({
          ...widget,
          sqlQuery: widget.sqlQuery || sql,
          sqlFields: {
            dimensions: discoveredDims,
            metrics: discoveredMets,
            fields,
          },
          query: {
            ...(widget.query || {}),
            sql: widget.query?.sql || sql,
            select: (fields.length
              ? fields.map((f) => (typeof f === 'string' ? f : f?.key))
              : [...discoveredDims, ...discoveredMets]
            ).filter(Boolean),
          },
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // Hydrate empty sql/fields from joinConfig or discover once per widget.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget?.id, widget?.type, widget?.sqlFields, widget?.query?.select, widget?.sqlQuery, widget?.joinConfig]);

  useEffect(() => {
    if (!widget || shouldSkipWidgetDataFetch(widget.type)) {
      setShowJoinModal(false);
      return;
    }
    if (widgetUsesReportQueries(widget)) {
      setShowJoinModal(false);
      return;
    }
    const sql = getWidgetSql(widget);
    const hasEvidence = !!(
      sql
      || widget.joinConfig?.tables?.length
      || widget.tableConfig?.columns?.length
      || widget.query?.select?.length
      || widget.sqlFields?.fields?.length
      || widget.sqlFields?.dimensions?.length
    );
    setShowJoinModal(!hasEvidence);
  }, [widget?.id, widget?.dataSource]);

  if (!widget) return null;

  const editorKind = getWidgetEditorKind(widget.type);
  const isPreset = editorKind === EDITOR_KIND.preset;
  const isChart = editorKind === EDITOR_KIND.chart || editorKind === EDITOR_KIND.pie;
  const isPie = editorKind === EDITOR_KIND.pie;

  const update = (patch) => onChange({ ...widget, ...patch });
  const updateQuery = (qPatch) => update({ query: { ...(widget.query || {}), ...qPatch } });
  const updateChartConfig = (cPatch) => update({ chartConfig: { ...(widget.chartConfig || {}), ...cPatch } });
  const updateTableConfig = (tPatch) => update({ tableConfig: { ...(widget.tableConfig || {}), ...tPatch } });

  const selectedExpressions = parseSelected(widget.query?.select);
  const usesReportQueries = widgetUsesReportQueries(widget);
  const reportQueryList = normalizeReportQueries(reportQueries);
  const discoveredKeys = (widget.sqlFields?.fields || [])
    .map((f) => (typeof f === 'string' ? f : f?.key))
    .filter(Boolean);
  const parsedKeys = parseSqlSelectKeys(widgetSql);
  const sqlFieldKeys = (
    parsedKeys.length
      ? parsedKeys
      : discoveredKeys.length
        ? discoveredKeys
        : [
          ...columns.dimensions,
          ...columns.metrics,
          ...(widget.query?.select || []),
          ...selectedExpressions.map((s) => s.label),
        ]
  ).filter((v, i, a) => v && a.indexOf(v) === i);
  const queryFields = fieldsForWidget(widget, reportQueryList, sqlFieldKeys);
  const fieldOptions = visibleColumnKeys(queryFields.map((f) => f.key));
  const formulaFields = queryFields.length
    ? queryFields
    : fieldOptions.map((k) => ({ key: k, fa: colLabel(k) }));
  const reportFields = queryFields;
  const selectedQuery = selectedReportQuery(widget, reportQueryList);

  const handleJoinApply = ({ sql, joinConfig, fields, dimensions, metrics }) => {
    setColumns({ dimensions: dimensions || [], metrics: metrics || [] });
    update(applySqlToWidget(widget, { sql, joinConfig, fields, dimensions, metrics }));
  };

  // Chart series
  const series = widget.chartConfig?.series || [];
  const addSeries = () => {
    const used = series.map((s) => s.key);
    const metrics = (columns.metrics.length
      ? columns.metrics
      : selectedExpressions.filter((s) => s.isMetric).map((s) => s.label));
    const next = metrics.find((m) => !used.includes(m)) || '';
    updateChartConfig({ series: [...series, { key: next, label: colLabel(next), color: '#6366f1' }] });
  };
  const updateSeries = (i, patch) => updateChartConfig({ series: series.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const removeSeries = (i) => updateChartConfig({ series: series.filter((_, idx) => idx !== i) });

  // Table columns
  const tableCols = widget.tableConfig?.columns || [];
  const applyTableColumns = (nextCols, { syncSql = true } = {}) => {
    const labeled = (nextCols || []).map(withPersianLabel);
    if (!syncSql) {
      updateTableConfig({ columns: labeled });
      return;
    }
    const sql = syncWidgetSqlFromTableColumns({ ...widget, tableConfig: { ...(widget.tableConfig || {}), columns: labeled } }, labeled);
    const keys = parseSqlSelectKeys(sql);
    update({
      tableConfig: {
        ...(widget.tableConfig || {}),
        columns: labeled,
        pageSize: widget.tableConfig?.pageSize || 20,
      },
      sqlQuery: sql || widget.sqlQuery,
      query: {
        ...(widget.query || {}),
        sql: sql || widget.query?.sql,
        select: keys.length ? keys : (widget.query?.select || []),
      },
    });
  };
  const SQL_COL_FIELDS = new Set(['key', 'math', 'template', 'compute', 'source']);
  const addTableCol = () => applyTableColumns([...tableCols, defaultTableColumn()]);
  const addCustomCol = () => applyTableColumns([
    ...tableCols,
    {
      ...defaultTableColumn(),
      source: 'custom',
      key: nextCustomColumnKey(tableCols),
      label: 'ستون سفارشی',
      format: 'number',
      compute: 'math',
      math: '',
    },
  ]);
  const updateTableCol = (i, patch) => {
    const nextCols = tableCols.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    const syncSql = Object.keys(patch).some((k) => SQL_COL_FIELDS.has(k));
    applyTableColumns(nextCols, { syncSql });
  };
  const removeTableCol = (i) => applyTableColumns(tableCols.filter((_, idx) => idx !== i));
  const moveTableCol = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tableCols.length) return;
    const next = [...tableCols];
    [next[i], next[j]] = [next[j], next[i]];
    applyTableColumns(next, { syncSql: false });
  };
  const updateAppearance = (patch) => update({ appearance: { ...(widget.appearance || {}), ...patch } });

  return (
    <>
      <aside className="w-full h-full flex flex-col bg-white overflow-hidden rtl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-l from-indigo-50 to-white shrink-0">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-base">{isPie ? '🥧' : '📦'}</span>
            <span className="text-sm font-bold text-slate-800 truncate max-w-[180px]">
              {widget.title || widget.type}
            </span>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none shrink-0">✕</button>
        </div>

        {widgetErrors.length > 0 && (
          <div className="shrink-0 border-b border-red-200 bg-red-50 px-3 py-2 space-y-1" role="alert">
            <p className="text-[11px] font-bold text-red-700">این ویجت برای ذخیره کامل نیست:</p>
            {widgetErrors.map((err, i) => (
              <p key={`${err.field}-${i}`} className="text-[11px] leading-relaxed text-red-600">
                {err.message}
              </p>
            ))}
          </div>
        )}

        {layout && (
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 shrink-0">
            <p className="text-xs font-bold text-slate-400 mb-2">اندازه ویجت</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-slate-500 mb-1">عرض</p>
                <div className="flex flex-wrap gap-1">
                  {WIDTH_PRESETS.map((p) => (
                    <button key={p.w} type="button" onClick={() => onLayoutChange({ w: p.w })}
                      className={cn('rounded px-1.5 py-0.5 text-xs transition-colors border',
                        layout.w === p.w ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-indigo-50')}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">ارتفاع</p>
                <div className="flex flex-wrap gap-1">
                  {HEIGHT_PRESETS.map((p) => (
                    <button key={p.h} type="button" onClick={() => onLayoutChange({ h: p.h })}
                      className={cn('rounded px-1.5 py-0.5 text-xs transition-colors border',
                        layout.h === p.h ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-indigo-50')}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
        <Section title="عمومی" defaultOpen>
          <Field label="عنوان">
            <input className={inputCls} value={widget.title || ''} onChange={(e) => update({ title: e.target.value })} placeholder="عنوان ویجت" />
          </Field>
          <Field label="ظاهر هدر / پس‌زمینه">
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {APPEARANCE_PRESETS.map((p) => {
                const on = matchWidgetTheme(widget.appearance)?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    title={p.label}
                    onClick={() => update({ appearance: appearanceFromTheme(p, widget.appearance) })}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] transition-colors',
                      on
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    <span
                      className="h-3.5 w-3.5 rounded-full border border-black/10 shrink-0"
                      style={{ background: `linear-gradient(135deg, ${p.swatch[0]} 50%, ${p.swatch[1]} 50%)` }}
                    />
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <ColorField label="هدر" value={widget.appearance?.headerBg || ''} onChange={(v) => updateAppearance({ headerBg: v })} />
              <ColorField label="متن هدر" value={widget.appearance?.headerFg || ''} onChange={(v) => updateAppearance({ headerFg: v })} />
              <ColorField label="بدنه" value={widget.appearance?.bodyBg || ''} onChange={(v) => updateAppearance({ bodyBg: v })} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="text-[10px] text-slate-400">قاب ویجت در صفحه گزارش</p>
              <button
                type="button"
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[10px] font-bold',
                  isNakedWidgetChrome(widget.appearance)
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50',
                )}
                onClick={() => update({ appearance: applyNakedWidgetChrome(widget.appearance, !isNakedWidgetChrome(widget.appearance)) })}
              >
                {isNakedWidgetChrome(widget.appearance) ? 'با قاب' : 'بدون قاب'}
              </button>
            </div>
            <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5">
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={widget.appearance?.showHeader !== false}
                  onChange={(e) => updateAppearance({ showHeader: e.target.checked })}
                />
                هدر ویجت
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={widget.appearance?.showBorder !== false}
                  onChange={(e) => updateAppearance({ showBorder: e.target.checked })}
                />
                حاشیه ویجت
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={widget.appearance?.showPadding !== false}
                  onChange={(e) => updateAppearance({ showPadding: e.target.checked })}
                />
                فاصله داخلی ویجت
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={!!widget.appearance?.fitContent}
                  onChange={(e) => updateAppearance({ fitContent: e.target.checked })}
                />
                اندازه متناسب با محتوا
              </label>
            </div>
          </Field>
          {!isPreset && (
            <Field label="سطح دسترسی (شغل)">
              <select
                className={selectCls}
                value={String(widget.scopeLevel || jobOptions[0]?.level || 1)}
                onChange={(e) => update({ scopeLevel: Number(e.target.value) })}
              >
                {jobOptions.map((j) => (
                  <option key={j.id} value={j.level}>
                    {j.name} — سطح {j.level}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </Section>

        {!isPreset && (
          <Section title="منبع داده" defaultOpen>
            <Field label="نوع منبع">
              <select
                className={selectCls}
                value={usesReportQueries ? REPORT_DATA_SOURCE : 'sql'}
                onChange={(e) => {
                  const src = e.target.value;
                  if (src === REPORT_DATA_SOURCE) {
                    const reportQueryId = widget.reportQueryId
                      || (reportQueryList.length === 1 ? reportQueryList[0].id : undefined);
                    const patch = { dataSource: REPORT_DATA_SOURCE, reportQueryId };
                    if (widget.type === 'table' && reportQueryId) {
                      const q = reportQueryList.find((item) => item.id === reportQueryId);
                      const keys = catalogFieldsFromQueries(q ? [q] : []).map((f) => f.key);
                      if (keys.length) {
                        patch.tableConfig = {
                          ...(widget.tableConfig || {}),
                          columns: syncTableColumnsFromQueryKeys(
                            widget.tableConfig?.columns || [],
                            keys,
                            [],
                          ),
                        };
                      }
                    }
                    update(patch);
                  } else {
                    update({ dataSource: 'sql', reportQueryId: undefined });
                  }
                }}
              >
                <option value={REPORT_DATA_SOURCE}>کوئری گزارش (یک‌بار اجرا)</option>
                <option value="sql">کوئری اختصاصی این ویجت</option>
              </select>
            </Field>
            {usesReportQueries && (
              <>
                <Field label="کوئری منبع">
                  <select
                    className={selectCls}
                    value={selectedQuery?.id || widget.reportQueryId || ''}
                    onChange={(e) => {
                      const reportQueryId = e.target.value || undefined;
                      const patch = { reportQueryId };
                      if (widget.type === 'table' && reportQueryId) {
                        const q = reportQueryList.find((item) => item.id === reportQueryId);
                        const keys = catalogFieldsFromQueries(q ? [q] : []).map((f) => f.key);
                        if (keys.length) {
                          patch.tableConfig = {
                            ...(widget.tableConfig || {}),
                            columns: syncTableColumnsFromQueryKeys(
                              widget.tableConfig?.columns || [],
                              keys,
                              [],
                            ),
                          };
                        }
                      }
                      update(patch);
                    }}
                  >
                    <option value="">— انتخاب کوئری —</option>
                    {reportQueryList.map((q) => (
                      <option key={q.id} value={q.id}>{q.name || q.id}</option>
                    ))}
                  </select>
                </Field>
                <p className="text-[10px] leading-relaxed text-slate-400">
                  فقط فیلدهای همین کوئری در انتخاب ستون و فرمول دیده می‌شوند.
                </p>
                {reportFields.length > 0 ? (
                  <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-1.5">
                    <p className="text-[10px] font-bold text-emerald-700 mb-1">فیلدهای کوئری انتخاب‌شده</p>
                    <div className="flex max-h-28 flex-wrap gap-1 overflow-auto">
                      {reportFields.map((f) => (
                        <span key={`${f.queryId}-${f.key}`} className="rounded-full border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] text-slate-600" title={f.key}>
                          {f.fa || f.key}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800">
                    {reportQueryList.length
                      ? 'یک کوئری را انتخاب کنید تا فیلدهایش اینجا بیاید.'
                      : 'هنوز کوئری گزارشی ساخته نشده. از تب «داده» یک کوئری اضافه کنید.'}
                  </p>
                )}
              </>
            )}
          </Section>
        )}

        {!isPreset && !usesReportQueries && (
          <Section title="کوئری" defaultOpen badge={fieldError('sqlQuery') ? '!' : undefined}>
            <button
              type="button"
              onClick={() => setShowJoinModal(true)}
              className="w-full rounded-lg border border-indigo-200 bg-indigo-50 py-2.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100"
            >
              ⊕ ساخت / ویرایش کوئری
            </button>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              انتخاب جدول و ستون، متن SQL را همزمان عوض می‌کند. جای‌گذاری دستی هم ذخیره می‌شود؛ بعد از آن با انتخاب دوباره، کوئری بازنویسی می‌گردد.
            </p>
            {fieldError('sqlQuery') && (
              <p className="text-[10px] text-red-500 leading-relaxed">{fieldError('sqlQuery')}</p>
            )}
            {queryTables.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {queryTables.map((t) => (
                  <span key={t.alias} className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-mono text-slate-700">
                    {t.alias}→{t.name}
                  </span>
                ))}
              </div>
            )}
            {widgetSql && !usesPeriod && (
              <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 leading-relaxed">
                فیلتر سال / ماه / روز بالای گزارش روی این ویجت اثر ندارد مگر در کوئری از
                {' '}<span className="font-mono" dir="ltr">:year</span>،
                {' '}<span className="font-mono" dir="ltr">:month</span>
                {' '}یا{' '}<span className="font-mono" dir="ltr">:day</span> استفاده شود.
              </p>
            )}
            {widgetSql ? (
              <pre className="rounded-md bg-slate-900 text-emerald-300 text-[9px] p-2 overflow-auto max-h-48 dir-ltr text-left whitespace-pre-wrap">
                {widgetSql}
              </pre>
            ) : (
              <p className="text-[10px] text-slate-400">هنوز کوئری تنظیم نشده — از مودال جداول را JOIN کنید یا SQL را جای‌گذاری کنید.</p>
            )}
            {(columns.dimensions.length > 0 || columns.metrics.length > 0) && (
              <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-1.5">
                <p className="text-[10px] font-bold text-emerald-700 mb-1">فیلدهای کوئری</p>
                <div className="flex flex-wrap gap-1">
                  {columns.dimensions.map((d) => (
                    <span key={d} className="rounded-full bg-white border border-slate-200 px-1.5 py-0.5 text-[9px] text-slate-600">{d}</span>
                  ))}
                  {columns.metrics.map((m) => (
                    <span key={m} className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] text-indigo-700">{m}</span>
                  ))}
                </div>
              </div>
            )}
            <Field label="حداکثر ردیف">
              <input
                type="number"
                className={inputCls}
                value={widget.query?.limit || 100}
                min={1}
                max={QUERY_LIMIT_MAX}
                onChange={(e) => updateQuery({ limit: Number(e.target.value) })}
              />
              <p className="text-[10px] text-slate-400 mt-0.5">تا {QUERY_LIMIT_MAX.toLocaleString('fa-IR')} ردیف از دیتابیس</p>
            </Field>
          </Section>
        )}

        {/* Pie-specific config */}
        {isPie && (
          <Section title="تنظیم نمودار دایره‌ای" defaultOpen badge={fieldError('chartConfig') ? '!' : undefined}>
            <Field label="دسته (برچسب برش)">
              <select
                className={cn(selectCls, fieldError('chartConfig.xKey') && 'border-red-400 ring-1 ring-red-200')}
                value={widget.chartConfig?.xKey || ''}
                onChange={(e) => updateChartConfig({ xKey: e.target.value })}
              >
                <option value="">— انتخاب —</option>
                {fieldOptions.map((c) => <option key={c} value={c}>{colLabel(c)}</option>)}
              </select>
              {fieldError('chartConfig.xKey') && (
                <p className="text-[10px] text-red-500 mt-0.5">{fieldError('chartConfig.xKey')}</p>
              )}
            </Field>
            <Field label="مقدار (اندازه برش)">
              <select
                className={cn(selectCls, fieldError('chartConfig.series') && 'border-red-400 ring-1 ring-red-200')}
                value={series[0]?.key || ''}
                onChange={(e) => {
                  const key = e.target.value;
                  updateChartConfig({
                    series: [{ key, label: colLabel(key), color: series[0]?.color || '#6366f1' }],
                  });
                }}
              >
                <option value="">— انتخاب —</option>
                {fieldOptions.map((c) => (
                  <option key={c} value={c}>{colLabel(c)}</option>
                ))}
              </select>
              {fieldError('chartConfig.series') && (
                <p className="text-[10px] text-red-500 mt-0.5">{fieldError('chartConfig.series')}</p>
              )}
            </Field>
            <Field label="تعداد اعشار">
              <select
                className={selectCls}
                value={widget.chartConfig?.decimals ?? 0}
                onChange={(e) => updateChartConfig({ decimals: Number(e.target.value) })}
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Field>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              یک بُعد (مثل برند) + یک معیار تجمیعی را در کوئری انتخاب کنید.
            </p>
          </Section>
        )}

        {/* Other chart config */}
        {isChart && !isPie && (
          <Section
            title="تنظیم نمودار"
            defaultOpen={!!fieldError('chartConfig')}
            badge={fieldError('chartConfig') ? '!' : undefined}
          >
            <Field label="کلید محور X">
              <select
                className={cn(selectCls, fieldError('chartConfig.xKey') && 'border-red-400 ring-1 ring-red-200')}
                value={widget.chartConfig?.xKey || ''}
                onChange={(e) => updateChartConfig({ xKey: e.target.value })}
              >
                <option value="">— انتخاب —</option>
                {fieldOptions.map((c) => <option key={c} value={c}>{colLabel(c)}</option>)}
              </select>
              {fieldError('chartConfig.xKey') && (
                <p className="text-[10px] text-red-500 mt-0.5">{fieldError('chartConfig.xKey')}</p>
              )}
            </Field>
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
              <input type="checkbox" checked={!!widget.chartConfig?.stacked}
                onChange={(e) => updateChartConfig({ stacked: e.target.checked })} className="accent-indigo-600" />
              نمودار انباشته (Stacked)
            </label>
            <Field label="تعداد اعشار">
              <select
                className={selectCls}
                value={widget.chartConfig?.decimals ?? 0}
                onChange={(e) => updateChartConfig({ decimals: Number(e.target.value) })}
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Field>

            <div className="space-y-2">
              <p className={cn(
                'text-xs font-medium',
                fieldError('chartConfig.series') ? 'text-red-600' : 'text-slate-500',
              )}>
                سری‌های نمودار
              </p>
              {fieldError('chartConfig.series') && (
                <p className="text-[10px] text-red-500">{fieldError('chartConfig.series')}</p>
              )}
              {series.map((s, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-2.5 space-y-2 bg-slate-50">
                  <div className="flex items-center gap-1">
                    <select className={`${selectCls} flex-1`} value={s.key}
                      onChange={(e) => updateSeries(i, { key: e.target.value, label: colLabel(e.target.value) })}>
                      <option value="">— انتخاب معیار —</option>
                      {fieldOptions.map((m) => (
                        <option key={m} value={m}>{colLabel(m)}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => removeSeries(i)} className="text-slate-400 hover:text-red-500 shrink-0">✕</button>
                  </div>
                  <input className={inputCls} placeholder="برچسب سری" value={s.label}
                    onChange={(e) => updateSeries(i, { label: e.target.value })} />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 shrink-0">رنگ:</label>
                    <input type="color" value={s.color || '#6366f1'}
                      onChange={(e) => updateSeries(i, { color: e.target.value })}
                      className="h-7 w-12 rounded cursor-pointer border-0" />
                    {widget.type === 'composed_chart' && (
                      <select className={`${selectCls} flex-1`} value={s.type || 'bar'}
                        onChange={(e) => updateSeries(i, { type: e.target.value })}>
                        <option value="bar">میله</option>
                        <option value="line">خط</option>
                        <option value="area">ناحیه</option>
                      </select>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" onClick={addSeries}
                className="w-full rounded-lg border border-dashed border-indigo-300 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50">
                + افزودن سری
              </button>
            </div>
          </Section>
        )}

        {editorKind === EDITOR_KIND.table && (
          <Section title="تنظیم جدول" defaultOpen badge={tableCols.length || null}>
            <Field label="تعداد ردیف در هر صفحه">
              <select
                className={selectCls}
                value={widget.tableConfig?.pageSize || 20}
                onChange={(e) => updateTableConfig({ pageSize: Number(e.target.value) })}
              >
                {TABLE_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} ردیف</option>
                ))}
              </select>
            </Field>
            <Field label="اندازه ستون‌ها">
              <select
                className={selectCls}
                value={resolveColumnSizeMode(widget.tableConfig)}
                onChange={(e) => {
                  const columnSizeMode = e.target.value;
                  updateTableConfig({
                    columnSizeMode,
                    fitColumns: columnSizeMode === 'fill',
                  });
                }}
              >
                {COLUMN_SIZE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Field>
            <p className="text-[10px] text-slate-500 leading-relaxed -mt-1">
              {COLUMN_SIZE_MODES.find((m) => m.value === resolveColumnSizeMode(widget.tableConfig))?.hint}
            </p>
            <TableLookFields
              tableConfig={widget.tableConfig}
              onChange={updateTableConfig}
            />
            <TableColumnsEditor
              columns={tableCols}
              fieldOptions={fieldOptions}
              onUpdate={updateTableCol}
              onRemove={removeTableCol}
              onMove={moveTableCol}
              onAddField={addTableCol}
              onAddCustom={addCustomCol}
            />
          </Section>
        )}

        {editorKind === EDITOR_KIND.stat && (
          <Section title="تنظیم کارت KPI" defaultOpen>
            <KpiCardEditor
              widget={widget}
              fieldOptions={fieldOptions}
              catalogFields={formulaFields}
              onChange={(statConfig) => update({ statConfig })}
            />
          </Section>
        )}

        {editorKind === EDITOR_KIND.gauge && (
          <Section title={widget.type === 'progress_bar' ? 'تنظیم نوار پیشرفت' : 'تنظیم گیج'} defaultOpen>
            <GaugeEditor
              widget={widget}
              fieldOptions={formulaFields}
              onChange={(patch) => update(patch)}
            />
          </Section>
        )}
        </div>
      </aside>

      <TableJoinModal
        key={widget.id}
        open={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        tablesCatalog={sqlTables}
        initialConfig={widget.joinConfig || null}
        initialSql={widgetSql}
        tableColumns={tableCols}
        onApply={handleJoinApply}
      />
    </>
  );
}
